import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import {
  ApiNestedProduct,
  ApiProductCatalogItem,
} from '../../../../core/models/product-api.models';
import { ImageApiService } from '../../../../core/services/image-api.service';
import { ProductApiService } from '../../../../core/services/product-api.service';

interface DevisCard {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  accentColor: string;
  nestedCount: number;
}

@Component({
  selector: 'app-devis',
  templateUrl: './devis.component.html',
  styleUrls: ['./devis.component.scss'],
})
export class DevisComponent implements OnInit, OnDestroy {
  loading = true;
  errorMessage = '';
  toastMessage = '';
  logoUrl = 'assets/images/logo.svg';
  backgroundUrl = '';
  cards: DevisCard[] = [];
  selectedType: ApiProductCatalogItem | null = null;

  private productTypeId = '';
  private catalog: ApiProductCatalogItem[] = [];
  private toastTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly destroy$ = new Subject<void>();
  private readonly accentColors = ['#1B7A4E', '#F0A500', '#E53935', '#1E88E5'];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly productApi: ProductApiService,
    private readonly imageApi: ImageApiService,
  ) {}

  get isSubProductView(): boolean {
    return !!this.productTypeId;
  }

  get pageTitle(): string {
    return this.isSubProductView ? 'Souscription' : 'Devis';
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.productTypeId = params.get('productTypeId') ?? '';
      this.load();
    });
  }

  ngOnDestroy(): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.loading = true;
    this.errorMessage = '';
    this.selectedType = null;
    this.cards = [];

    forkJoin({
      catalog: this.productApi.list(),
      images: this.imageApi.list().pipe(catchError(() => of([]))),
      logoUrl: this.imageApi
        .getUrlByName('company_logo', this.logoUrl)
        .pipe(catchError(() => of(this.logoUrl))),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ catalog, images, logoUrl }) => {
          this.catalog = catalog;
          this.logoUrl = logoUrl || this.logoUrl;
          this.backgroundUrl =
            images.find(
              (item) => (item.name || '').toLowerCase().trim() === 'quick_access_bg_image',
            )?.url || '';

          if (this.productTypeId) {
            this.selectedType = catalog.find((item) => item.id === this.productTypeId) ?? null;
            if (!this.selectedType) {
              this.loading = false;
              this.errorMessage = 'Produit introuvable.';
              return;
            }
            this.cards = (this.selectedType.products ?? []).map((product, index) =>
              this.toNestedCard(product, index),
            );
          } else {
            this.cards = catalog.map((item, index) => this.toTypeCard(item, index));
          }

          this.loading = false;
          if (!this.cards.length) {
            this.errorMessage = this.isSubProductView
              ? 'Aucune formule disponible pour ce produit.'
              : 'Aucun produit disponible pour le moment.';
          }
        },
        error: () => {
          this.loading = false;
          this.errorMessage = 'Impossible de charger les produits.';
        },
      });
  }

  onCardClick(card: DevisCard): void {
    if (this.isSubProductView) {
      if (!this.selectedType) {
        return;
      }
      this.router.navigate(['/souscription', this.selectedType.id], {
        queryParams: { nested: card.id },
      });
      return;
    }

    const productType = this.catalog.find((item) => item.id === card.id);
    if (!productType?.products?.length) {
      this.showToast("La souscription n'est pas encore possible sur ce produit.");
      return;
    }

    this.router.navigate(['/devis', productType.id]);
  }

  goBack(): void {
    this.router.navigate(['/devis']);
  }

  onLogoError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = 'assets/images/logo.svg';
  }

  onCardImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = 'assets/products/product-auto.jpg';
  }

  sentenceCase(value: string): string {
    const text = (value || '').trim();
    if (!text) {
      return '';
    }
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  trackByCardId(_index: number, card: DevisCard): string {
    return card.id;
  }

  private toTypeCard(item: ApiProductCatalogItem, index: number): DevisCard {
    return {
      id: item.id,
      name: this.sentenceCase(item.name),
      description: this.sentenceCase(item.description || ''),
      imageUrl: this.productApi.optimizeImageUrl(item.image?.url, { width: 160, height: 160 }),
      accentColor: this.accentColors[index % this.accentColors.length],
      nestedCount: item.products?.length ?? 0,
    };
  }

  private toNestedCard(product: ApiNestedProduct, index: number): DevisCard {
    return {
      id: product.id,
      name: this.sentenceCase(product.name),
      description: this.sentenceCase(product.description || ''),
      imageUrl: this.productApi.optimizeImageUrl(product.image?.url, { width: 160, height: 160 }),
      accentColor: this.accentColors[index % this.accentColors.length],
      nestedCount: product.pages?.length ?? 0,
    };
  }

  private showToast(message: string): void {
    this.toastMessage = message;
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
    this.toastTimer = setTimeout(() => {
      this.toastMessage = '';
      this.toastTimer = null;
    }, 3000);
  }
}
